import React, { useState } from "react";

import { AlertCircle, Lightbulb } from "lucide-react";

import {
  ExperimentType,
  contentByExperimentType,
} from "./researchFoundation.constants";

interface PrimaryHypothesisFieldProps {
  value: string;
  onChange: (value: string) => void;
  experimentType: ExperimentType;
  error?: string;
}

export const PrimaryHypothesisField: React.FC<PrimaryHypothesisFieldProps> = ({
  value,
  onChange,
  experimentType,
  error,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const config = contentByExperimentType[experimentType];

  const handleFocus = () => {
    setIsFocused(true);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Lightbulb size={16} className="text-blue-400" />
          <span className="text-sm font-semibold text-white">
            Primary Hypothesis
            <span className="ml-1 text-green-500">*</span>
          </span>
        </div>
      </div>

      <div className="space-y-2">
        <div className="grid grid-cols-3 gap-2 text-[10px] uppercase tracking-wide text-white/40">
          <span>If [change]...</span>
          <span>Then [outcome]...</span>
          <span>Because [reason]...</span>
        </div>

        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={handleFocus}
          onBlur={() => setIsFocused(false)}
          placeholder={config.primaryHypothesis.placeholder}
          rows={4}
          className={`w-full resize-none rounded-lg border bg-white/5 px-3 py-2 text-white placeholder:text-white/40 transition-all duration-300 focus:outline-none ${
            error
              ? "border-red-400/50 ring-1 ring-red-400/20"
              : isFocused
                ? "border-amber-400/50 ring-1 ring-amber-400/30"
                : "border-white/20"
          }`}
        />
      </div>

      <p className="text-xs text-white/50">
        Structure your hypothesis using: If [change], then [outcome], because
        [assumption]. This makes it testable and actionable.
      </p>

      {error && (
        <div className="flex items-center gap-2">
          <AlertCircle size={14} className="text-red-400" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}
    </div>
  );
};
